import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import Select from 'react-select';
import { toast } from 'react-toastify';
import CompanyOwnerSelect from '../CompanyOwnerSelect';
import { APPLY_TO_JOB } from '../../Pages/job-order/gql';
import { GET_JOB_ORDERS_FOR_COMPANY } from '../../Pages/recruiter-folder/gql';
import { GET_JOB_APPLICANT } from '../../Pages/candidate/gql';
import { customSelectDropDown } from '../styled/customSelect';
import { formatDateUS } from '../../utils/commonFn';
import { JobAppStatusEnum } from '../../utils/constant';
import { AppContext } from '../../context';

interface Props {
  candidateId: string;
  /** The job-applicant id of the application this modal is currently open on,
   *  excluded from the "Other Application History" list. */
  currentApplicantId?: string;
  /** Fired after a successful apply (a new JobApplicant was created). The
   *  recsys Candidate Search page uses this to drop the candidate from its
   *  current results list when they applied to the selected job. */
  onApplied?: (candidateId: string, jobOrderId: string) => void;
  /** Prefills the company + job selects with the host page's current
   *  selection (recsys Candidate Search passes its selected company/job so
   *  applying to the current job order is one click). The recruiter can still
   *  change both. Absent (e.g. /new-applicants) — no prefill, unchanged. */
  defaultApplyTarget?: { companyId: string; companyName: string; jobOrderId: string } | null;
  /** JobApplicant.source recorded when the apply creates a new application.
   *  Host pages that represent a distinct sourcing channel pass their own
   *  (candidate-recommendations passes "Candidate Rec"); absent — the backend
   *  default "Recruiter" applies. */
  applySource?: string;
}

const jobOrderLabel = (item: any) => {
  const loc = item?.companyLocation;
  const locStr = loc
    ? [loc.city, loc.state, loc.country].filter(Boolean).join(', ')
    : '';
  return locStr ? `${item.jobTitle} - ${locStr}` : item.jobTitle;
};

const humanizeStatus = (status?: string) =>
  status ? (JobAppStatusEnum as any)[status] || status.replace(/_/g, ' ') : '--';

// Bootstrap badge colour by application status.
const statusBadgeClass = (status?: string) => {
  switch (status) {
    case 'APPLIED':
    case 'KEEP':
    case 'IN_KEEP_LIST':
      return 'badge-success';
    case 'EMAILED':
    case 'CALLED':
    case 'CALL_SCHEDULED':
    case 'SUBMITTED':
    case 'SUBMITTED_TO_CLIENT':
      return 'badge-info';
    case 'CONTACT_LATER':
    case 'INTERVIEWED':
      return 'badge-warning';
    case 'OFFERED':
    case 'PLACED':
      return 'badge-primary';
    case 'DECLINED':
    case 'REJECTED':
    case 'CANDIDATE_DECLINE':
    case 'CLIENT_DECLINE':
      return 'badge-danger';
    default:
      return 'badge-secondary';
  }
};

const ApplyTab: FC<Props> = ({ candidateId, currentApplicantId, onApplied, defaultApplyTarget, applySource }) => {
  const { theme } = useContext(AppContext);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  // Job order to auto-select once its company's job options finish loading —
  // the company-change effect below clears the job and fetches asynchronously,
  // so the default job can only be applied after the options arrive.
  const [pendingDefaultJobId, setPendingDefaultJobId] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  // Synchronous guard: `applying` state updates async, so two fast clicks can
  // both pass the canApply check and create duplicate applications.
  const submitting = useRef(false);

  const [fetchJobOrders, { data: jobData, loading: loadingJobs }] = useLazyQuery(
    GET_JOB_ORDERS_FOR_COMPANY
  );
  const [fetchHistory, { data: historyData, loading: loadingHistory }] =
    useLazyQuery(GET_JOB_APPLICANT, { fetchPolicy: 'network-only' });

  const [applyToJob] = useMutation(APPLY_TO_JOB);

  const loadHistory = () => {
    if (!candidateId) return;
    fetchHistory({
      variables: { id: candidateId, page: 1, limit: 50 },
    });
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  // Prefill from the host page's current selection (one-shot per candidate —
  // CandidateWorkspacePanel keys this tab by candidate id, so a new candidate
  // remounts and re-prefills). Setting the company kicks the company-change
  // effect below, which fetches the job options; the pending id then picks the
  // default job once they arrive.
  useEffect(() => {
    if (!defaultApplyTarget) return;
    setSelectedCompany({
      label: defaultApplyTarget.companyName,
      value: { companyId: defaultApplyTarget.companyId },
    });
    setPendingDefaultJobId(defaultApplyTarget.jobOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  useEffect(() => {
    // Clear the dependent job whenever the company changes so a stale selection
    // from a previous company can never be applied.
    setSelectedJob(null);
    const companyId = selectedCompany?.value?.companyId;
    if (companyId) {
      fetchJobOrders({ variables: { companyId, limit: 100, page: 1 } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const jobOptions = (jobData?.findJobOrdersForCompany?.jobOrders ?? []).map(
    (item: any) => ({ label: jobOrderLabel(item), value: { jobOrderId: item.id } })
  );

  // Apply the pending default job once its company's options have loaded. If
  // the default job isn't among the fetched options (closed/filled since), the
  // pending id is dropped and the job select is simply left empty.
  useEffect(() => {
    if (!pendingDefaultJobId || loadingJobs || !jobData) return;
    const match = jobOptions.find((o: any) => o?.value?.jobOrderId === pendingDefaultJobId);
    if (match) setSelectedJob(match);
    setPendingDefaultJobId(null);
    // jobOptions is derived from jobData (not memoized) — depending on jobData
    // instead keeps this effect from re-running every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDefaultJobId, loadingJobs, jobData]);

  // Every other application this candidate has (any stage, incl. Declined),
  // excluding the one this modal is currently open on, ordered by most recently
  // updated so a just-applied / just-changed job lands at the top on refresh.
  const applications: any[] = (historyData?.getJobApplicants?.jobApplicant ?? [])
    .filter((app: any) => app?.id !== currentApplicantId)
    .slice()
    .sort(
      (a: any, b: any) =>
        new Date(b?.updatedAt ?? b?.createdAt ?? 0).getTime() -
        new Date(a?.updatedAt ?? a?.createdAt ?? 0).getTime()
    );

  const canApply = !!candidateId && !!selectedCompany && !!selectedJob && !applying;

  const handleApply = async () => {
    if (!canApply || submitting.current) return;
    submitting.current = true;
    const jobOrderId = selectedJob?.value?.jobOrderId;
    const jobLabel = selectedJob.label;
    setApplying(true);
    try {
      const res = await applyToJob({
        variables: { candidateId, jobOrderId, source: applySource },
      });
      const payload = res?.data?.createSubmissionOnApply;
      if (payload?.status === 201) {
        toast.error(
          `Job applicant already exists with status ${humanizeStatus(
            payload?.jobApplicant?.JobAppStatus
          )}`
        );
        return;
      }
      const newApplicantId = payload?.jobApplicant?.id;
      if (!newApplicantId) {
        toast.error('Failed to apply candidate');
        return;
      }

      // Apply already creates the new application with the "Applied" status,
      // so there's nothing more to set here.
      toast.success(`Applied to ${jobLabel}`);
      onApplied?.(candidateId, jobOrderId);

      setSelectedCompany(null);
      setSelectedJob(null);
      loadHistory();
    } catch {
      toast.error('Failed to apply candidate');
    } finally {
      setApplying(false);
      submitting.current = false;
    }
  };

  return (
    <div className="workspace-apply">
      <strong className="text-14">Apply to Another Job</strong>

      <div className="workspace-apply__field">
        <label className="workspace-apply__label">Job Order</label>
        <CompanyOwnerSelect
          value={selectedCompany}
          placeholder="Select…"
          onChange={(opt: any) => {
            setSelectedCompany(opt);
            setSelectedJob(null);
          }}
        />
      </div>

      <div className="workspace-apply__field">
        <label className="workspace-apply__label">Select Job</label>
        <Select
          value={selectedJob}
          onChange={(opt: any) => setSelectedJob(opt)}
          options={jobOptions}
          isLoading={loadingJobs}
          isDisabled={!selectedCompany || !jobOptions.length}
          placeholder="Select job"
          classNamePrefix="select"
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          styles={{
            ...customSelectDropDown(theme),
            menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
          }}
        />
        {selectedCompany && !loadingJobs && !jobOptions.length && (
          <span className="text-danger" style={{ fontSize: 12 }}>
            No active job order found for {selectedCompany.label}
          </span>
        )}
      </div>

      <button
        type="button"
        className="btn btn-warning btn-block workspace-apply__btn"
        disabled={!canApply}
        onClick={handleApply}
      >
        {applying ? 'Applying…' : 'Apply'}
      </button>

      <hr className="workspace-apply__divider" />

      <strong className="text-14">
        Other Application(s){applications.length ? ` · ${applications.length}` : ''}
      </strong>

      {loadingHistory && applications.length === 0 ? (
        <p className="text-muted mt-2 mb-0" style={{ fontSize: 13 }}>Loading history…</p>
      ) : applications.length === 0 ? (
        <p className="text-muted mt-2 mb-0" style={{ fontSize: 13 }}>No other applications.</p>
      ) : (
        <div className="workspace-apply__history">
          {applications.map((app) => {
            const status = app?.JobAppStatus;
            return (
              <div key={app.id} className="workspace-apply__history-item">
                <div className="workspace-apply__history-main">
                  {/* Same "title - city, state" convention as the job select
                      above (GET_JOB_APPLICANT already selects the job order's
                      companyLocation) — jobs without a location show the title
                      alone. */}
                  <span
                    className="workspace-apply__history-title"
                    title={app?.JobOrder?.jobTitle ? jobOrderLabel(app.JobOrder) : undefined}
                  >
                    {app?.JobOrder?.jobTitle ? jobOrderLabel(app.JobOrder) : '--'}
                  </span>
                  <span className="text-muted workspace-apply__history-sub">
                    {[app?.JobOrder?.company?.name, app.createdAt ? `Applied ${formatDateUS(app.createdAt)}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {app.updatedAt && (
                    <span className="text-muted workspace-apply__history-updated">
                      Updated {formatDateUS(app.updatedAt)}
                    </span>
                  )}
                </div>
                <span className={`badge ${statusBadgeClass(status)}`}>
                  {humanizeStatus(status)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApplyTab;
